// @ts-nocheck
import {
    BlockVariant,
    BlockVariantBaseProps,
    blockVariantOptions,
    BlockVariantProps,
    BlockVariantSide,
    blockVariantSideOptions,
    getVariantCss,
} from "@store/Library/Components/Toolkit/Block/variant";
import breakpointValues, { BreakpointValue } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import {
    DataOption,
    DataOptions,
    getDataOptionThemeValue,
    getDataOptionValue,
} from "@toolkit/Framework/Common/Utilities/dataOptions";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { LiteralUnion, RecursivePartial, RequiredSpecific } from "@toolkit/Framework/Types/Common";
import { StyledTheme } from "@toolkit/Framework/Types/StyledTheme";
import { siteWidthStyles, stretchStyles } from "@toolkit/Themes/common/mixins";
import React, { Children, FC, HTMLAttributes } from "react";
import styled, { css, useTheme } from "styled-components";

// -- INTERFACES

export type BlockMarginValue = BreakpointValue[];
export type BlockMarginOption = DataOption<BlockMarginValue | string>;
export interface BlockMarginOptionMapper {
    narrow: BlockMarginOption;
    spaced: BlockMarginOption;
    none: BlockMarginOption;
}
export type BlockMarginOptions = DataOptions<BlockMarginOption> & BlockMarginOptionMapper;
export type BlockMarginOptionValue = LiteralUnion<keyof BlockMarginOptionMapper>;
export type BlockMargin = BlockMarginValue | BlockMarginOptionValue;

export interface BlockBorderProps {
    width: string;
    type: string;
    color: string;
}

export interface BlockBaseProps {
    fullWidth?: boolean;
    variant?: BlockVariant;
    variantProps?: RecursivePartial<BlockVariantProps>;
    variantSide?: BlockVariantSide;
    margin?: BlockMargin;
    preserveSpacing?: boolean;
    stretch?: boolean;
}

export interface BlockParsedBaseProps extends RequiredSpecific<BlockBaseProps, "variantProps"> {
    margin: BlockMarginValue;
    variantProps: BlockVariantProps;
}

export interface BlockProps extends BlockBaseProps, BlockVariantBaseProps {}
export interface BlockParsedProps extends BlockParsedBaseProps, BlockVariantBaseProps {}

export const blockMarginOptions: BlockMarginOptions = {
    narrow: { id: "Narrow", displayName: "Narrow", value: "design.layout.spacing.narrowContent" },
    spaced: { id: "spaced", displayName: "Spaced", value: "design.layout.spacing.spacedContent" },
    none: { id: "none", displayName: "None", value: [0] },
};

export const variantDefaultProps: BlockVariantProps = Object.entries(blockVariantOptions).reduce(
    (result, [key, option]) => {
        result[key] = option.type.defaultProps;
        return result;
    },
    {} as any,
);

const parseProps = (
    {
        fullWidth,
        variant,
        variantProps,
        variantSide,
        margin,
        preserveSpacing,
        containerCss,
        spacerCss,
        wrapperCss,
        stretch,
        ...otherProps
    }: Partial<BlockProps>,
    theme: StyledTheme,
) => {
    const blockProps: BlockParsedProps = {
        fullWidth,
        variant: getDataOptionValue(blockVariantOptions, variant),
        variantProps: mergeProps(variantDefaultProps, variantProps),
        variantSide: getDataOptionValue(blockVariantSideOptions, variantSide),
        margin: getDataOptionThemeValue(blockMarginOptions, margin, theme) || theme.design.layout.spacing.content || [],
        preserveSpacing,
        containerCss,
        spacerCss,
        wrapperCss,
        stretch,
    };

    return { blockProps, otherProps };
};

// -- COMPONENTS

export const BlockWrapper = styled.div<BlockParsedProps>`
    ${({ stretch, wrapperCss }) => css`
        ${stretch ? stretchStyles() : null}
        ${wrapperCss}
    `}

    ${getVariantCss("wrapperCss")}
`;

export const BlockSpacer = styled.div<BlockParsedProps>`
    ${siteWidthStyles()}
    width: 100%;

    ${({ margin, fullWidth, stretch, spacerCss }) => css`
        ${breakpointValues(margin, ["margin-top", "margin-bottom"])}
        ${fullWidth ? "max-width: none;" : null}
        ${stretch ? stretchStyles() : null}
        ${spacerCss}
    `}

    ${getVariantCss("spacerCss")}
`;

export const BlockContainer = styled.div<BlockParsedProps>`
    ${({ theme }) => css`
        & & {
            > ${BlockSpacer} {
                padding-left: 0;
                padding-right: 0;

                &:not([data-preserve]) {
                    ${cssProp("margin-top", theme.design.typography.spacing.base)}
                    ${cssProp("margin-bottom", theme.design.typography.spacing.base)}
                }
            }

            &:first-child > ${BlockSpacer} {
                margin-top: 0;
            }

            &:last-child > ${BlockSpacer} {
                margin-bottom: 0;
            }

            &::before,
            &::after {
                display: none;
            }
        }
    `}

    ${({ stretch, containerCss }) => css`
        ${stretch ? stretchStyles() : null}
        ${containerCss}
    `}

    ${getVariantCss("containerCss")}
`;

const Block: FC<BlockProps & HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
    const theme = useTheme();

    if (!Children.count(children)) {
        return null;
    }

    const { blockProps, otherProps } = parseProps(props, theme);

    return (
        <BlockContainer {...otherProps} {...blockProps}>
            <BlockSpacer {...blockProps} data-preserve={blockProps.preserveSpacing || null}>
                <BlockWrapper {...blockProps}>{children}</BlockWrapper>
            </BlockSpacer>
        </BlockContainer>
    );
};

Block.displayName = "Block";

export default Block;
